/**
 * NEC 2023 Table 314.16(B) — Volume allowance required per conductor (cubic
 * inches), by conductor AWG. Used by the box-fill calculator per the counting
 * rules in 314.16(B)(1)–(5).
 *
 * Note: box fill only spans 18 AWG → 6 AWG (Table 314.16(B) does not list
 * larger conductors; boxes for those are sized by 314.28). This is a separate,
 * smaller AWG domain than the ampacity/voltage-drop tables, so it has its own
 * size union.
 */
export const BOX_FILL_AWG_SIZES = ["18", "16", "14", "12", "10", "8", "6"] as const;
export type BoxFillAwg = (typeof BOX_FILL_AWG_SIZES)[number];

export const VOLUME_ALLOWANCE_IN3: Record<BoxFillAwg, number> = {
  "18": 1.5,
  "16": 1.75,
  "14": 2.0,
  "12": 2.25,
  "10": 2.5,
  "8": 3.0,
  "6": 5.0,
};

export function isBoxFillAwg(value: unknown): value is BoxFillAwg {
  return typeof value === "string" && (BOX_FILL_AWG_SIZES as readonly string[]).includes(value);
}

/** Volume allowance (in³) for a single conductor of the given size. */
export function volumeAllowance(awg: BoxFillAwg): number {
  return VOLUME_ALLOWANCE_IN3[awg];
}

/**
 * Rank of a box-fill AWG size for "largest conductor" comparisons. Larger
 * physical conductor (smaller AWG number / 6 is biggest) → higher rank.
 */
export function boxFillSizeRank(awg: BoxFillAwg): number {
  return BOX_FILL_AWG_SIZES.indexOf(awg);
}
